import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SignalBadge from "@/components/SignalBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatRoas, signalMeta, actionPlan } from "@/lib/signal";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { FilePlus2, TrendingUp, TrendingDown, Minus, Lock, ClipboardList, Sparkles, AlertTriangle, X, Settings } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [dismissedFlip, setDismissedFlip] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => {
      setData(r.data);
      if (!r.data.brand) navigate("/onboarding/brand");
      else if (!r.data.logs?.length) navigate("/onboarding/first-log");
    }).finally(() => setLoading(false));
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, [navigate]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/dashboard",
      });
      window.location.href = data.portal_url;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status ? `Portal failed (HTTP ${err.response.status})` : null) ||
        err?.message ||
        "Could not open subscription portal";
      toast.error(String(detail));
      setPortalLoading(false);
    }
  };

  if (loading) return <Shell><div className="text-slate-400">Loading…</div></Shell>;
  if (!data?.latest) return null;

  const { latest, logs, brand } = data;
  const meta = signalMeta(latest.signal);
  const TrendIcon = latest.trend === "rising" ? TrendingUp : latest.trend === "falling" ? TrendingDown : Minus;

  const chartData = logs.slice(-4).map((l, i) => ({ week: `W${i + 1}`, roas: l.roas, be: l.break_even_roas }));

  return (
    <Shell>
      <div className="mb-2 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{brand?.store_name}</p>
            <Link
              to="/onboarding/brand?edit=1"
              data-testid="edit-brand-link"
              className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900 underline decoration-dotted underline-offset-2"
            >
              Edit
            </Link>
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-1">
            This week's signal
          </h1>
        </div>
        <Link to="/log" data-testid="dashboard-log-cta">
          <Button className="bg-slate-900 hover:bg-slate-800 h-11">
            <FilePlus2 className="mr-2 h-4 w-4" /> Log this week
          </Button>
        </Link>
      </div>

      {user?.has_stripe_customer && (
        <div className="mt-2 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={openBillingPortal}
            disabled={portalLoading}
            data-testid="manage-subscription-button"
            className="text-slate-500 hover:text-slate-900"
          >
            <Settings className="mr-2 h-3.5 w-3.5" />
            {portalLoading ? "Opening…" : "Manage subscription"}
          </Button>
        </div>
      )}

      {data?.flipped && !dismissedFlip && (
        <Card
          className="mt-4 p-4 sm:p-5 rounded-2xl border-2 border-amber-300 bg-amber-50"
          data-testid="signal-flip-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Signal flipped: {(data.from_signal || "—").toUpperCase()} → {(latest.signal || "—").toUpperCase()}
              </p>
              <p className="text-sm text-amber-800 mt-1">
                We emailed your action plan. Act quickly — your numbers changed direction this week.
              </p>
            </div>
            <button
              onClick={() => setDismissedFlip(true)}
              className="text-amber-700 hover:text-amber-900 p-1"
              aria-label="Dismiss"
              data-testid="signal-flip-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </Card>
      )}

      {/* Big signal card */}
      <Card className={`mt-6 p-8 sm:p-12 rounded-2xl border ${meta.className}`} data-testid="dashboard-signal-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] opacity-80">Signal</p>
            <h2 className="font-display font-bold text-6xl sm:text-7xl tracking-tight mt-2" data-testid="dashboard-signal-label">
              {meta.label}
            </h2>
          </div>
          <SignalBadge signal={latest.signal} size="lg" />
        </div>
        <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="dashboard-signal-reason">
          {latest.reason}
        </p>
      </Card>

      {/* Action Plan card - unlocked during trial and for Pro users */}
      <ActionPlanCard signal={latest.signal} buffer={latest.buffer} isPro={Boolean(user?.has_access)} />

      {/* Metric grid */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="ROAS" value={formatRoas(latest.roas)} testid="metric-roas" />
        <Metric label="Break-even ROAS" value={formatRoas(latest.break_even_roas)} testid="metric-be" />
        <Metric
          label="Buffer"
          value={(latest.buffer >= 0 ? "+" : "") + formatRoas(latest.buffer)}
          accent={latest.buffer >= 0 ? "text-emerald-600" : "text-red-600"}
          testid="metric-buffer"
        />
        <Metric
          label="Trend"
          value={latest.trend ? latest.trend.charAt(0).toUpperCase() + latest.trend.slice(1) : "—"}
          icon={<TrendIcon className="h-5 w-5" />}
          testid="metric-trend"
        />
      </div>

      {/* Mini chart */}
      <Card className="mt-6 p-6 rounded-2xl border-slate-200 bg-white">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display font-semibold text-lg text-slate-900">Last 4 weeks</h3>
          <span className="text-xs text-slate-500">ROAS vs break-even</span>
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }} />
              <Line type="monotone" dataKey="be" stroke="#94a3b8" strokeDasharray="4 4" dot={false} strokeWidth={1.5} />
              <Line type="monotone" dataKey="roas" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: "#0f172a" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {user && !user.is_pro && (
        <Card className="mt-6 p-6 rounded-2xl bg-slate-900 text-white" data-testid="dashboard-upgrade-banner">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display font-semibold text-xl">
                  {user.in_trial ? "You're on the Pro trial" : "Your trial has ended"}
                </p>
                {pricing?.is_founding && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 text-[10px] font-bold tracking-widest uppercase">
                    Founding · {pricing.spots_left} left
                  </span>
                )}
              </div>
              {pricing && (
                <p className="text-slate-300 text-sm mt-2">
                  <span className="font-display font-bold text-2xl text-white" data-testid="dashboard-upgrade-price">
                    ${Number.isInteger(pricing.current_price) ? pricing.current_price : pricing.current_price.toFixed(2)}
                  </span>
                  <span className="text-slate-400">/month</span>
                  {pricing.is_founding && (
                    <span className="ml-2 line-through text-slate-500 text-sm">${pricing.regular_price}</span>
                  )}
                  <span className="mx-2 text-slate-500">·</span>
                  {user.in_trial
                    ? `${user.trial_days_left} day${user.trial_days_left === 1 ? "" : "s"} left — keep everything you're seeing`
                    : "Upgrade to keep your data and Pro features"}
                </p>
              )}
            </div>
            <Link to="/upgrade" data-testid="dashboard-upgrade-cta">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 h-11">
                {pricing?.is_founding ? "Lock in founding rate" : "Upgrade to Pro"}
              </Button>
            </Link>
          </div>
        </Card>
      )}
    </Shell>
  );
}

const Metric = ({ label, value, accent = "text-slate-900", icon, testid }) => (
  <Card className="lift p-5 rounded-2xl bg-white border-slate-200">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <div className="mt-2 flex items-center gap-2">
      {icon}
      <p className={`font-display font-bold text-3xl ${accent}`} data-testid={testid}>{value}</p>
    </div>
  </Card>
);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <Header />
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      {children}
    </main>
  </div>
);

const ActionPlanCard = ({ signal, buffer, isPro }) => {
  const plan = actionPlan(signal, buffer);
  const accent =
    signal === "scale" ? "text-emerald-700" : signal === "stop" ? "text-red-700" : "text-amber-700";

  if (!isPro) {
    return (
      <Card className="mt-6 p-6 sm:p-8 rounded-2xl bg-white border-slate-200 relative overflow-hidden" data-testid="action-plan-card-locked">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-slate-500" />
            <p className="font-display font-semibold text-lg text-slate-900">Action Plan</p>
            <span className="inline-flex items-center gap-1 ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <Lock className="h-3 w-3" /> Locked
            </span>
          </div>
        </div>
        <div className="relative">
          <div className="filter blur-[6px] pointer-events-none select-none" aria-hidden="true">
            <p className={`font-display font-semibold text-xl ${accent}`}>{plan.headline}</p>
            <p className="mt-2 text-slate-600 leading-relaxed">{plan.body}</p>
          </div>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <p className="text-slate-700 font-medium" data-testid="action-plan-upgrade-text">
                Upgrade to Pro to unlock your action plan
              </p>
              <Link to="/upgrade" className="inline-block mt-3" data-testid="action-plan-upgrade-cta">
                <Button className="bg-slate-900 hover:bg-slate-800 h-10">
                  <Sparkles className="mr-2 h-4 w-4" /> Unlock
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-6 p-6 sm:p-8 rounded-2xl bg-white border-slate-200" data-testid="action-plan-card">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="h-4 w-4 text-slate-500" />
        <p className="font-display font-semibold text-lg text-slate-900">Action Plan</p>
      </div>
      <p className={`font-display font-semibold text-xl ${accent}`} data-testid="action-plan-headline">
        {plan.headline}
      </p>
      <p className="mt-2 text-slate-700 leading-relaxed" data-testid="action-plan-body">
        {plan.body}
      </p>
    </Card>
  );
};
